// src/.../InvoiceDisplayer.tsx
import React, { useEffect, useState } from 'react';
import { InvoiceRecord, InvoiceFilters } from './types';
import {
  getAllInvoicesByLocalId,
  moveRejectedToPending,
} from '../../../../../../crud/management_api/invoices';
import { formatCurrency } from '../../../../../../utils/functions';
import Validator from './components/Validator/Validator';
import FilterBar from './components/FilterBar/FilterBar';
import InvoiceFilterOffCanvas from './components/InvoiceFilterOffCanvas/InvoiceFilterOffCanvas';
import DeleteInvoice from './components/DeleteInvoice/DeleteInvoice';
import CreateInvoice from './components/CreateInvoice/CreateInvoice';
import {
  FaTrash,
  FaFileInvoiceDollar,
} from 'react-icons/fa6';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './InvoiceDisplayer.scss';
import { useNotifications } from '../../../../../Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/NotificationContext';

// ——— Utilities for highlighting matches ———

const escapeRegExp = (s: string) =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} className="highlight">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

// ——— Date formatting helpers ———

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const fmt = (d: Date) => d.toISOString().split('T')[0];

interface InvoiceDisplayerProps {
  fetchPurchases: () => void;
}

const InvoiceDisplayer: React.FC<InvoiceDisplayerProps> = ({ fetchPurchases }) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [filtered, setFiltered] = useState<InvoiceRecord[]>([]);
  const [filters, setFilters] = useState<InvoiceFilters>({
    searchText: '',
    startDate: fmt(thirtyDaysAgo),
    endDate: fmt(tomorrow),
    minTotal: 0,
    maxTotal: Infinity,
    providerId: 0,
    status: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [movingToPending, setMovingToPending] = useState<Set<number>>(new Set()); // Track which invoices are being moved
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRecord | null>(
    null
  );
  const [showValidator, setShowValidator] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const notificationsCtx = useNotifications();

  // Fetch invoices whenever the date range changes
  useEffect(() => {
    if (!filters.startDate || !filters.endDate) {
      setInvoices([]);
      setFiltered([]);
      return;
    }

    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = Number(user.local_id) || 0;

    getAllInvoicesByLocalId(localId, filters.startDate, filters.endDate)
      .then((res) => setInvoices(res.data as InvoiceRecord[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters.startDate, filters.endDate]);

  // Refrescar facturas si llega una notificación con topic === 'facturas' y local_id coincide
  useEffect(() => {
    const notif = notificationsCtx?.lastNotification;
    if (!notif) return;
    if (typeof notif.topic !== 'string') return;
    if (notif.topic.toLowerCase() !== 'facturas') return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = Number(user.local_id) || 0;
    if (Number(notif.local_id) !== localId) return;
    // No mostrar loading spinner
    const startDate = filters.startDate || '';
    const endDate = filters.endDate || '';
    getAllInvoicesByLocalId(localId, startDate, endDate)
      .then((res) => setInvoices(res.data as InvoiceRecord[]))
      .catch(console.error);
    // Limpiar la notificación para evitar loops
    notificationsCtx.setLastNotification(null);
  }, [notificationsCtx, notificationsCtx?.lastNotification, filters.startDate, filters.endDate]);

  // Apply status + search + range + provider filters
  useEffect(() => {
    let baseList = invoices;
    if (filters.status === 'pending') {
      baseList = invoices.filter((i) => !i.approved && !i.rejected);
    } else if (filters.status === 'approved') {
      baseList = invoices.filter((i) => i.approved);
    } else {
      baseList = invoices.filter((i) => i.rejected);
    }

    let out = baseList;

    // — live search on UUID, provider name, or total —
    if (filters.searchText) {
      const low = filters.searchText.toLowerCase();
      out = out.filter((i) => {
        const nameMatch = i.provider_name
          ?.toLowerCase()
          .includes(low);
        const totalMatch = i.total
          .toString()
          .toLowerCase()
          .includes(low);
        const uuidMatch = i.invoice_uuid
          .toLowerCase()
          .includes(low);
        return Boolean(nameMatch || totalMatch || uuidMatch);
      });
    }

    // total range
    out = out.filter(
      (i) => i.total >= filters.minTotal && i.total <= filters.maxTotal
    );
    // provider filter
    if (filters.providerId > 0) {
      out = out.filter((i) => i.provider_id === filters.providerId);
    }

    setFiltered(out);
  }, [filters, invoices]);

  const stats = {
    pending: invoices.filter((i) => !i.approved && !i.rejected).length,
    approved: invoices.filter((i) => i.approved).length,
    rejected: invoices.filter((i) => i.rejected).length,
  };

  const handleValidationClose = () => {
    setShowValidator(false);
    // trigger a quick refetch if needed
    setTimeout(() => setFilters((f) => ({ ...f })), 500);
  };

  // ——— Here's the updated handler ———
  const handleMoveRejectedToPending = (inv: InvoiceRecord) => {
    // Add invoice to loading set
    setMovingToPending(prev => new Set(prev).add(inv.invoice_id));
    
    moveRejectedToPending(inv.invoice_id)
      .then(() => {
        // 1) Update local state
        setInvoices((prev) =>
          prev.map((i) =>
            i.invoice_id === inv.invoice_id
              ? { ...i, rejected: false }
              : i
          )
        );
        // 2) Switch to Pendientes status
        setFilters(prev => ({ ...prev, status: 'pending' }));
        toast.success(
          'Factura movida de Rechazadas a Pendientes con éxito.'
        );
      })
      .catch((err) => {
        console.error('Error moving invoice:', err);
        toast.error(
          'Error al mover la factura de Rechazadas a Pendientes.'
        );
      })
      .finally(() => {
        // Remove invoice from loading set
        setMovingToPending(prev => {
          const newSet = new Set(prev);
          newSet.delete(inv.invoice_id);
          return newSet;
        });
      });
  };  // Al eliminar una factura, actualizar localmente
  const handleInvoiceDeleted = (invoiceId: number) => {
    setInvoices((prev) => prev.filter((i) => i.invoice_id !== invoiceId));
    setShowDelete(false);
  };

  if (loading) {
    return (
      <div className="invoice-displayer-loading">
        <LoadingSpinner text="Cargando facturas..." />
      </div>
    );
  }

  return (
    <div className="invoice-page">
      <FilterBar
        invoices={invoices}
        filters={filters}
        onFilterChange={setFilters}
        statusCounts={stats}
        onShowFilters={() => setShowFilters(true)}
      />

      <div className="invoice-grid">
        {filtered.length > 0 ? (
          filtered.map((inv) => (
            <div key={inv.invoice_id} className="invoice-card">
              <div
                className="card-image"
                style={{ backgroundImage: `url(${inv.file_url})` }}
              />
              <div className="card-body">
                <div className="card-header">
                  <span
                    className={`status-badge ${
                      inv.approved ? 'approved' : 'pending'
                    }`}
                  >
                    {inv.approved ? 'Aprobado' : 'Pendiente'}
                  </span>
                  <span className="card-id">
                    ...{inv.invoice_uuid.slice(-6)}
                  </span>
                </div>
                <h3 className="card-title">
                  {highlight(
                    inv.provider_name || 'Proveedor Desconocido',
                    filters.searchText
                  )}
                </h3>
                <p className="card-info">
                  {formatDate(inv.timestamp)} • {inv.product_count}{' '}
                  productos
                </p>
                <p className="card-total">
                  {highlight(
                    formatCurrency(inv.total),
                    filters.searchText
                  )}
                </p>
              </div>
              <div className="card-actions">
                {/* Mostrar botón Validar solo si la factura NO está aprobada NI rechazada */}
                {!inv.approved && !inv.rejected && (
                  <Button
                    variant="none"
                    className="validate-invoice"
                    onClick={() => {
                      setSelectedInvoice(inv);
                      setShowValidator(true);
                    }}
                  >
                    Validar
                  </Button>
                )}

                {inv.rejected && (
                  <Button
                    variant="none"
                    className="move-to-pending"
                    onClick={() => handleMoveRejectedToPending(inv)}
                    disabled={movingToPending.has(inv.invoice_id)}
                  >
                    {movingToPending.has(inv.invoice_id) ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          style={{ marginRight: '0.5rem' }}
                        />
                        Moviendo...
                      </>
                    ) : (
                      'Mover a pendientes'
                    )}
                  </Button>
                )}

                <Button
                  variant="icon"
                  className="delete"
                  onClick={() => {
                    setSelectedInvoice(inv);
                    setShowDelete(true);
                  }}
                >
                  <FaTrash />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-invoices-found">
            <FaFileInvoiceDollar size={48} />
            <h4>No se encontraron facturas</h4>
            <p>Intenta ajustar los filtros o subir una nueva factura.</p>
          </div>
        )}
      </div>

      {selectedInvoice && (
        <Validator
          invoice={selectedInvoice}
          show={showValidator}
          onClose={handleValidationClose}
          onValidated={(invoiceId) => {
            setInvoices((prev) =>
              prev.map((i) =>
                i.invoice_id === invoiceId ? { ...i, approved: true, rejected: false } : i
              )
            );
            setFilters(prev => ({ ...prev, status: 'approved' }));
          }}
          fetchPurchases={fetchPurchases}
        />
      )}
      {selectedInvoice && (
        <DeleteInvoice
          invoiceId={selectedInvoice.invoice_id}
          show={showDelete}
          onClose={() => setShowDelete(false)}
          onDeleted={handleInvoiceDeleted}
        />
      )}
      {showCreate && (
        <CreateInvoice
          show={showCreate}
          handleClose={() => setShowCreate(false)}
        />
      )}

      {/* New Offcanvas Filter Component */}
      <InvoiceFilterOffCanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        invoices={invoices}
        filters={filters}
        onFilterChange={setFilters}
        statusCounts={stats}
      />
    </div>
  );
};

export default InvoiceDisplayer;
